<template>
  <div class="PopUp">

    <pre class="PopUp-header t-small"
      v-if="content.text"
      v-html="content.text">
    </pre>

    <div class="PopUp-content">

      <div class="PopUp-overlays">

        <transition-group name="PopUp-overlay-">

          <div class="PopUp-overlay"
            v-for="(item, index) in content.options"
            key="index"
            ref="overlays"
            :class="`PopUp-overlay--${item.popup}`"
            v-show="activeIndex === index">

            <div class="PopUp-overlayItem"
              v-if="item.title1 || item.description1">

              <div class="PopUp-overlayItemHeader t-headingXs"
                v-if="item.title1"
                v-html="item.title1">
              </div>

              <pre class="PopUp-overlayItemBody t-capsSm"
                v-if="item.description1"
                v-html="item.description1">
              </pre>

            </div>

            <div class="PopUp-overlayItem"
              v-if="item.title2 || item.description2">

              <div class="PopUp-overlayItemHeader t-headingXs"
                v-if="item.title2"
                v-html="item.title2">
              </div>

              <pre class="PopUp-overlayItemBody t-capsSm"
                v-if="item.description2"
                v-html="item.description2">
              </pre>

            </div>

          </div>

        </transition-group>

      </div>

      <div class="PopUp-buttons">

        <div class="PopUp-button"
          v-for="(item, index) in content.options"
          ref="buttons"
          :style="buttonStyle(item)"
          @click="activate(index)">

          <expander :active="activeIndex === index" />

        </div>

      </div>

      <svg class="PopUp-lines" ref="lines" width="652" height="371"></svg>

      <img class="PopUp-image" :src="content.image" />

    </div>

  </div>
</template>

<script>
import Expander from 'components/Expander'
import * as d3 from 'd3'

export default {
  name: 'PopUp',

  components: {
    Expander
  },

  props: {
    content: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      activeIndex: false
    }
  },

  mounted () {
    this.$nextTick(this.initLines)
  },

  watch: {
    activeIndex () {
      this.updateLines()
    }
  },

  methods: {
    /**
     * Initialize lines
     */
    initLines () {
      this.lines = {
        rect: this.$refs.lines.getBoundingClientRect(),
        svg: d3.select(this.$refs.lines),
        current: false
      }
    },

    /**
     * Update line position
     */
    updateLines () {
      const $overlay = this.$refs.overlays[this.activeIndex]
      const $button = this.$refs.buttons[this.activeIndex]

      // Remove current lines
      if (this.lines.current) this.lines.current.remove()

      // Exit here if no active index
      if (this.activeIndex === false) return

      this.$nextTick(() => {
        this.drawLine($overlay, $button)
      })
    },

    /**
     * Draw line
     */
    drawLine ($overlay, $button) {
      const x2 = $button.offsetLeft > $overlay.offsetLeft
        ? $overlay.offsetLeft + $overlay.offsetWidth
        : $overlay.offsetLeft

      // Add line
      this.lines.current = this.lines.svg.append('line')
        .attr('class', this.class('line'))
        .attr('x1', $button.offsetLeft + ($button.offsetWidth / 2))
        .attr('y1', $button.offsetTop + ($button.offsetHeight / 2))
        .attr('x2', x2)
        .attr('y2', $overlay.offsetTop + ($overlay.offsetHeight / 2))
    },

    /**
     * Return button positions
     * @param  {Object} item
     * @return {Object}
     */
    buttonStyle (item) {
      return {
        top: `${item.hotspot.y}px`,
        left: `${item.hotspot.x}px`
      }
    },

    /**
     * Handle selection of a button
     * @param  {Object} index
     */
    activate (index) {
      if (this.activeIndex === index) {
        this.activeIndex = false
        return
      }

      this.activeIndex = index
    },

    /**
     * Return component-prefixed class
     * @param {String} name
     * @return {String}
     */
    class (name) {
      return `PopUp-${name}`
    }
  }
}
</script>

<style>
.PopUp {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 652px;
  text-align: center;
}

.PopUp-header {
  position: relative;
  z-index: 1;
  margin-bottom: 10px;
}

.PopUp-content {
  position: relative;
  width: 652px;
  height: 371px;
}

.PopUp-image {
  display: inline-block;
  max-width: 534px;
  max-height: 371px;
}

.PopUp-overlays {
  position: absolute;
  z-index: 300;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
}

.PopUp-overlay {
  position: absolute;
  z-index: 200;
  padding: 20px;
  width: 188px;
  border: 2px solid var(--color-3);
  background-color: rgba(255,255,255,0.8);
  text-align: center;
  border-radius: 24px;
}

.PopUp-overlay--enter-active,
.PopUp-overlay--leave-active {
  transition: all 0.25s ease;
  transform: scale(1);
}

.PopUp-overlay--enter,
.PopUp-overlay--leave-to {
  transform: scale(0.95);
  opacity: 0;
}

.PopUp-overlay--topLeft { top: 0px; left: 0px; }
.PopUp-overlay--topRight { top: 0px; right: 0px; }
.PopUp-overlay--bottomRight { bottom: 0px; right: 0px; }
.PopUp-overlay--bottomLeft { bottom: 0px; left: 0px; }

.PopUp-overlayItem {

}

.PopUp-overlayItem + .PopUp-overlayItem {
  margin-top: 1rem;
}

.PopUp-overlayItemHeader {
  color: var(--color-5);
}

.PopUp-overlayItemHeader + .PopUp-overlayItemBody {
  margin-top: 0.25rem;
}

.PopUp-overlayItemBody {
  line-height: 20px !important;
}

.PopUp-buttons {
  position: absolute;
  z-index: 200;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
}

.PopUp-button {
  position: absolute;
}

.PopUp-lines {
  position: absolute;
  z-index: 100;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.PopUp-line {
  stroke-width: 2px;
  stroke: var(--color-3);
  animation: popupLine 0.5s ease forwards;
  opacity: 0;
}

@keyframes popupLine {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
</style>



// WEBPACK FOOTER //
// PopUp.vue?69c181f1